/**
 * SelectCell — SELECT-variant editable cell. Uses SimpleSelect; the parent
 * CellShell hides SimpleSelect's internal label since the column header
 * already conveys the field name.
 */

import { useEffect, useState } from 'react';

import { SimpleSelect } from '@/components/ui/simple-select';

import CellShell from './CellShell';
import type { BaseCellProps } from './types';

interface SelectCellProps extends BaseCellProps {
  options?: Array<{ value: string; label: string }>;
}

const getSelectValue = (
  val: string | null,
): { value: string; label: string } | string => {
  if (val === null || val === '-') return { value: '-', label: '-' };
  return { value: val, label: val };
};

const SelectCell = ({
  value,
  id,
  name,
  isActive,
  isDisabled = false,
  isOptionalField = false,
  errorMessage,
  setData,
  options,
}: SelectCellProps) => {
  const [isChanged, setIsChanged] = useState(isActive);

  useEffect(() => {
    setIsChanged(isActive);
  }, [isActive]);

  const handleSelectChange = (_field: string, inputValue: string): void => {
    if (value !== inputValue) {
      setIsChanged(false);
      setData({ value: inputValue, id, name, isActive: false, errorMessage });
    } else {
      setIsChanged(isActive);
      setData({ value: inputValue, id, name, isActive, errorMessage });
    }
  };

  const placeholder =
    isOptionalField && isActive
      ? '-'
      : value === '-'
        ? value
        : value === null
          ? '-'
          : undefined;

  const selectValue =
    isOptionalField && isActive ? '-' : getSelectValue(value);

  return (
    <CellShell
      isActive={isActive}
      isOptionalField={isOptionalField}
      errorMessage={errorMessage}
      wrapperClassName="[&_label]:hidden"
      value={value}
    >
      <SimpleSelect
        options={options}
        name="data"
        md="12"
        controlId="data"
        ariaDescribedBy="table-select"
        placeholder={placeholder}
        value={selectValue}
        errors={isChanged}
        disabled={isDisabled}
        maxMenuHeight={180}
        setFieldValue={handleSelectChange}
        field="data"
      />
    </CellShell>
  );
};

export default SelectCell;
